import { useState } from 'react';
import { Seo } from '../components/Seo';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkle } from 'lucide-react';
import {
  BTW_FRACTIE,
  CHECKOUT_ADDONS,
  CHECKOUT_ADDON_IDS,
  FLOOR_BAND_MAX_L_M2,
  FLOOR_BAND_MAX_M_M2,
  FLOOR_BAND_MAX_S_M2,
  FLOOR_BAND_MAX_XL_M2,
  PRICING_PARTICULIER_BASIS_INCL_BTW,
  PRICING_PARTICULIER_INCL_BTW,
  PRICING_ZAKELIJK_BASIS_EX_BTW,
  PRICING_ZAKELIJK_EX_BTW,
  formatPriceEur,
} from '../lib/pricing';
import { PaymentMethodsPromoSection } from '../components/PaymentMethodsOverview';

type Audience = 'particulier' | 'zakelijk';

const BANDS = [
  { id: 'S' as const, rangeHome: `tot ${FLOOR_BAND_MAX_S_M2} m²` },
  { id: 'M' as const, rangeHome: `${FLOOR_BAND_MAX_S_M2 + 1}–${FLOOR_BAND_MAX_M_M2} m²` },
  { id: 'L' as const, rangeHome: `${FLOOR_BAND_MAX_M_M2 + 1}–${FLOOR_BAND_MAX_L_M2} m²` },
  { id: 'XL' as const, rangeHome: `${FLOOR_BAND_MAX_L_M2 + 1}–${FLOOR_BAND_MAX_XL_M2} m²` },
];

const BASIS_FEATURES = [
  'Vast opgebouwd leesrapport',
  'Indicatieve kW en jaarperspectief',
  'Geschikt om te printen / PDF',
];

const PLUS_FEATURES = [
  'Alles van Comfort Basis',
  'Uitgebreider leesrapport in begrijpelijke taal',
  'Handig om te delen in je project of met je opdrachtgever',
];

export function PricingPage() {
  const [audience, setAudience] = useState<Audience>('particulier');

  const isB2c = audience === 'particulier';

  return (
    <>
      <Seo
        path="/prijzen"
        title="Prijzen ComfortTool™"
        description="ComfortTool™: prijzen per m²-band (S–XL), Comfort Basis of Plus, particulier of zakelijk, optionele add-ons. Exact bedrag op de betaalpagina."
        keywords="ComfortTool prijzen, Comfort Basis, Comfort Plus, scan, btw, zakelijk, warmte koel"
      />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14">
        <div className="relative overflow-hidden rounded-3xl border border-ct-border bg-gradient-to-br from-ct-accent/[0.08] via-ct-surface to-ct-elevated/50 px-6 py-10 sm:px-10 sm:py-12">
          <div
            className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-ct-accent/15 blur-3xl"
            aria-hidden
          />
          <div className="relative max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-ct-accent">Prijzen</p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-ct-text sm:text-4xl lg:text-[2.5rem]">
              Eén overzicht. Jij kiest pas op de resultaatpagina.
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-ct-muted sm:text-base">
              Tarieven op band (S / M / L / XL) op basis van vloeroppervlakte. Op de resultaatpagina kies je{' '}
              <strong className="text-ct-text">Comfort Basis</strong> of <strong className="text-ct-text">Comfort Plus</strong>
              . <strong className="text-ct-text">Niet-woning (utiliteit)</strong> gebruikt dezelfde m²-grenzen. Boven{' '}
              {FLOOR_BAND_MAX_XL_M2} m²: offerte via contact.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/scan"
                className="inline-flex items-center gap-2 rounded-xl bg-ct-text px-6 py-3 text-sm font-bold text-ct-bg transition hover:opacity-90 dark:bg-white dark:text-slate-900"
              >
                Start de scan
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center rounded-xl border border-ct-border bg-ct-surface/90 px-6 py-3 text-sm font-semibold text-ct-text transition hover:border-ct-accent/35"
              >
                Groot volume? Neem contact op
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <PaymentMethodsPromoSection />
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
          <span className="mr-2 hidden text-sm font-medium text-ct-muted sm:inline">Weergave:</span>
          {(
            [
              { id: 'particulier' as const, label: 'Particulier', sub: 'incl. 21% btw' },
              { id: 'zakelijk' as const, label: 'Zakelijk', sub: `ex. btw · ${Math.round(BTW_FRACTIE * 100)}% apart` },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setAudience(t.id)}
              className={`rounded-full border px-4 py-2 text-left text-sm font-semibold transition ${
                audience === t.id
                  ? 'border-ct-accent bg-ct-accent/10 text-ct-text shadow-sm'
                  : 'border-ct-border bg-ct-surface/80 text-ct-muted hover:border-ct-accent/30'
              }`}
            >
              <span className="block">{t.label}</span>
              <span className="block text-[11px] font-normal opacity-80">{t.sub}</span>
            </button>
          ))}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-ct-border bg-ct-surface/90 p-6 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="rounded-lg bg-ct-elevated px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-ct-muted">
                Bundel
              </span>
              <h2 className="text-lg font-bold text-ct-text">Comfort Basis</h2>
            </div>
            <p className="mt-2 text-sm text-ct-muted">Strak, voorspelbaar — ideaal als je vooral de cijfers wilt.</p>
            <ul className="mt-5 space-y-2.5">
              {BASIS_FEATURES.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-ct-text">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-ct-success" aria-hidden />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-ct-accent/30 bg-gradient-to-br from-ct-accent/[0.07] to-ct-surface p-6 shadow-md">
            <Sparkle className="absolute right-4 top-4 h-6 w-6 text-ct-accent/40" aria-hidden />
            <div className="flex items-center gap-2">
              <span className="rounded-lg bg-ct-accent/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-ct-accent">
                Populair
              </span>
              <h2 className="text-lg font-bold text-ct-text">Comfort Plus</h2>
            </div>
            <p className="mt-2 text-sm text-ct-muted">Meer uitleg in lopende tekst — handig om te delen.</p>
            <ul className="mt-5 space-y-2.5">
              {PLUS_FEATURES.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-ct-text">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-ct-accent" aria-hidden />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <h2 className="mt-14 text-center text-xl font-bold text-ct-text sm:text-2xl">
          {isB2c ? 'Particulier — woning (incl. btw)' : 'Zakelijk — woning (ex. btw)'}
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-ct-muted">
          Het exacte bedrag zie je op de betaalpagina vlak voordat je betaalt.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {BANDS.map(({ id, rangeHome }) => (
            <div
              key={id}
              className="flex flex-col rounded-2xl border border-ct-border bg-ct-surface/95 p-5 shadow-sm transition hover:border-ct-accent/25 hover:shadow-md"
            >
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-xs font-bold uppercase tracking-wider text-ct-muted">Band {id}</p>
                <p className="text-[11px] text-ct-muted">{rangeHome}</p>
              </div>
              <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-ct-muted">Basis</p>
              <p className="text-xl font-bold tabular-nums text-ct-text">
                {isB2c
                  ? formatPriceEur(PRICING_PARTICULIER_BASIS_INCL_BTW[id])
                  : formatPriceEur(PRICING_ZAKELIJK_BASIS_EX_BTW[id], 0)}
                <span className="ml-1 text-xs font-normal text-ct-muted">{isB2c ? 'incl. btw' : 'ex. btw'}</span>
              </p>
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-ct-accent">Plus</p>
              <p className="text-xl font-bold tabular-nums text-ct-accent">
                {isB2c
                  ? formatPriceEur(PRICING_PARTICULIER_INCL_BTW[id])
                  : formatPriceEur(PRICING_ZAKELIJK_EX_BTW[id], 0)}
                <span className="ml-1 text-xs font-normal text-ct-muted">{isB2c ? 'incl. btw' : 'ex. btw'}</span>
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-dashed border-ct-border bg-ct-elevated/30 px-4 py-4 text-center text-sm text-ct-muted">
          <strong className="text-ct-text">Boven {FLOOR_BAND_MAX_XL_M2} m²:</strong> geen vaste webprijs —{' '}
          <Link to="/contact" className="font-semibold text-ct-accent hover:underline">
            vraag een offerte
          </Link>
          .
        </div>

        <p className="mt-6 text-center text-xs leading-relaxed text-ct-muted sm:text-sm">
          <strong className="text-ct-text">Utiliteit:</strong> band L tot {FLOOR_BAND_MAX_L_M2} m², XL{' '}
          {FLOOR_BAND_MAX_L_M2 + 1}–{FLOOR_BAND_MAX_XL_M2} m² — zelfde tarieven als hierboven voor die band; daarna
          offerte.
        </p>

        <h2 className="mt-16 text-xl font-bold text-ct-text">Optionele add-ons</h2>
        <p className="mt-2 max-w-3xl text-sm text-ct-muted">
          Kies je op de resultaatpagina; ze worden samen met je hoofdbundel op één betaaloverzicht gezet.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {CHECKOUT_ADDON_IDS.map((addonId) => {
            const a = CHECKOUT_ADDONS[addonId];
            return (
              <div
                key={addonId}
                className="rounded-2xl border border-ct-border bg-ct-surface/90 p-5 transition hover:border-ct-accent/20"
              >
                <p className="font-semibold text-ct-text">{a.titel}</p>
                <p className="mt-2 text-sm text-ct-muted">{a.beschrijving}</p>
                <div className="mt-4 flex flex-wrap gap-3 text-xs">
                  <span className="rounded-lg bg-ct-elevated px-2.5 py-1 font-medium text-ct-text">
                    Particulier {formatPriceEur(a.eurInclBtwB2C)} incl. btw
                  </span>
                  <span className="rounded-lg bg-ct-elevated px-2.5 py-1 font-medium text-ct-text">
                    Zakelijk {formatPriceEur(a.eurExBtwB2B, 0)} ex. btw
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-2xl border border-ct-border bg-ct-elevated/40 p-6 sm:p-8">
          <h3 className="text-base font-bold text-ct-text">Zakelijk volume of maatwerk?</h3>
          <p className="mt-2 text-sm text-ct-muted">
            Geen vaste webshop voor staffels of abonnementen — neem contact op voor factuur, maatwerk of meerdere rapporten.
          </p>
          <Link
            to="/contact"
            className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-ct-accent hover:underline"
          >
            Contact opnemen
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        <p className="mt-8 text-xs leading-relaxed text-ct-muted">
          Op de resultaatpagina: keuze Comfort Basis of Plus, een <strong className="text-ct-text">gratis tabel-overzicht</strong>{' '}
          om te downloaden, eventueel add-on tweede berekening. Zie ook{' '}
          <Link to="/voorwaarden" className="text-ct-accent hover:underline">
            voorwaarden
          </Link>
          .
        </p>
      </div>
    </>
  );
}
